import { Lock, Zap, ScanBarcode, Award, Crown, Gift, Printer, Scroll, History, ChevronLeft, ChevronRight, TrendingUp, Sparkles, Unlock, Loader2, CheckCircle2, ShieldCheck } from "lucide-react";
import { useState, useMemo } from "react";
import { useData } from "@/contexts/DataContext";
import { useAuth } from "@/hooks/use-auth";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { functions, db } from "@/lib/firebase";
import { httpsCallable } from "firebase/functions";
import { Checkout } from 'capacitor-razorpay';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";

// 🚨 IMPORTED: Master Plans and Rewards
import { PRO_PLANS, PHYSICAL_REWARDS } from "@/lib/constants";

export default function Rewards() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const {
    rewardPoints: dataPoints,
    rewardHistory: firestoreHistory,
    settingsLoaded,
    upgradeToProWithPoints,
    redeemPhysicalReward,
    userMode,
    proModeExpiry,
    setUserMode,
    businessProfile,
    openProUpgradeModal, // 🚨 BROUGHT IN THE GLOBAL TRIGGER
    subscriptionId,
    subscriptionStatus,
    plan,
  } = useData();
  const isUltimate = plan?.tier === "ultimate" && plan?.expiresAt && new Date(plan.expiresAt) > new Date();
  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState<"redeem" | "my_rewards" | "history">("redeem");
  const [selectedPlan, setSelectedPlan] = useState<{
    points: number;
    days: number;
    label: string;
  } | null>(null);

  const [selectedPhysicalReward, setSelectedPhysicalReward] = useState<{ id: string; name: string; points: number; price: number | null } | null>(null);
  const [showShippingForm, setShowShippingForm] = useState(false);
  const [showVoucherSuccess, setShowVoucherSuccess] = useState(false);
  const [shippingDetails, setShippingDetails] = useState({ fullName: "", phone: "", address: "", pincode: "" });
  const [isSubmittingReward, setIsSubmittingReward] = useState(false);
  const [showSwitchToProDialog, setShowSwitchToProDialog] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  const [earnHistoryCount, setEarnHistoryCount] = useState(25);

  const isCurrentlyPro = useMemo(() => {
    return userMode === "pro" && proModeExpiry && new Date(proModeExpiry) > new Date();
  }, [userMode, proModeExpiry]);

  // 🛡️ CRITICAL FIX: Lock digital passes if user is on active auto-pay
  const isControllableSub = !!subscriptionId && (subscriptionStatus === 'active' || subscriptionStatus === 'paused');

  const rewardHistory = useMemo(() => {
    return firestoreHistory
      .map((h) => ({ ...h, date: new Date(h.date) }))
      .sort((a, b) => b.date.getTime() - a.date.getTime());
  }, [firestoreHistory]);

  const myRewardsList = useMemo(() => {
    return rewardHistory.filter(item => {
      const desc = item.description.toLowerCase();
      const action = item.action || "";
      const isMajorRedemption = item.points < 0 && (action === "pro_upgrade" || action === "physical_reward" || desc.includes("upgrade") || desc.includes("voucher") || desc.includes("printer") || desc.includes("thermal"));
      return (
        isMajorRedemption ||
        action === "signup_bonus" ||
        desc.includes('welcome') ||
        desc.includes('sign-up') ||
        action === "referral_bonus" ||
        desc.includes('refer') ||
        desc.includes('invite')
      );
    });
  }, [rewardHistory]);

  const totalPoints = useMemo(() => {
    if (settingsLoaded) return dataPoints;
    return rewardHistory.reduce((acc, curr) => acc + curr.points, 0);
  }, [dataPoints, rewardHistory, settingsLoaded]);

  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      setLocation("/");
    }
  };

  const handleConfirmUpgrade = async () => {
    if (!selectedPlan) return;
    const success = await upgradeToProWithPoints(selectedPlan);
    if (success) {
      confetti({
        particleCount: 150,
        spread: 70,
        origin: { y: 0.6 },
        colors: ["#fbbf24", "#f59e0b", "#d97706", "#ffffff"],
      });
      toast({ title: isCurrentlyPro ? "Premium Extended!" : "Premium Activated!" });
      setActiveTab("history");
    }
    setSelectedPlan(null);
  };

  // 🚨 CORRECTED: Strict Payload validation with HARDCODED Key
  const handlePhysicalCashPayment = async (item: { id: string; name: string; price: number }) => {
    if (isProcessingPayment) return;
    setIsProcessingPayment(true);
    try {
      localStorage.setItem("dh_baseline_hardware", businessProfile?.lastHardwarePurchase || "");
      const createOrder = httpsCallable(functions, 'createRazorpayOrder');
      const response = await createOrder({ itemId: item.id, amount: item.price });
      const data = response.data as { success: boolean, orderId: string };

      if (!data.success || !data.orderId) throw new Error("Could not initialize order");

      // 🛡️ HARDCODED LIVE KEY
      const rzpKey = "rzp_live_StU8i2gpLCYQOq";

      const options: any = {
        key: rzpKey,
        order_id: data.orderId,
        name: "ASL DIGITAL SOLUTIONS",
        description: item.name,
        currency: "INR",
        amount: (item.price * 100).toString(),
        theme: { color: "#8B1A1A" },
      };

      const prefill: any = {};
      const contactNum = businessProfile?.businessPhone?.replace(/\D/g, '').slice(-10) || user?.phoneNumber?.replace(/\D/g, '').slice(-10);
      if (contactNum && contactNum.length >= 10) prefill.contact = contactNum;

      const emailStr = businessProfile?.businessEmail || user?.email;
      if (emailStr && emailStr.trim() !== "") prefill.email = emailStr.trim();

      const nameStr = businessProfile?.ownerName || businessProfile?.businessName || user?.displayName;
      if (nameStr && nameStr.trim() !== "") prefill.name = nameStr.trim();

      if (Object.keys(prefill).length > 0) {
        options.prefill = prefill;
      }

      await Checkout.open(options);
      setLocation("/payment-status");

    } catch (error: any) {
      if (error.code !== 0 && error.message !== "Payment cancelled by user") {
        toast({ title: "Payment Error", description: error.message || "Failed to open payment sheet.", variant: "destructive" });
      }
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handlePhysicalProceed = async () => {
    if (!selectedPhysicalReward) return;

    if (selectedPhysicalReward.id.includes("amazon")) {
      setIsSubmittingReward(true);
      const success = await redeemPhysicalReward(
        selectedPhysicalReward.id,
        selectedPhysicalReward.name,
        selectedPhysicalReward.points
      );
      setIsSubmittingReward(false);

      if (success) {
        confetti({ particleCount: 150, spread: 70, origin: { y: 0.6 }, colors: ["#f59e0b", "#fbbf24", "#ffffff"] });
        setShowVoucherSuccess(true);
        setActiveTab("my_rewards");
      } else {
        toast({ title: "Something went wrong", variant: "destructive" });
      }
    } else {
      setShowShippingForm(true);
    }
  };

  const handleShippingSubmit = async () => {
    if (!selectedPhysicalReward) return;
    setIsSubmittingReward(true);

    const success = await redeemPhysicalReward(
      selectedPhysicalReward.id,
      selectedPhysicalReward.name,
      selectedPhysicalReward.points,
      shippingDetails
    );

    setIsSubmittingReward(false);

    if (success) {
      confetti({ particleCount: 150, spread: 70, origin: { y: 0.6 }, colors: ["#8B1A1A", "#fbbf24", "#ffffff"] });
      toast({ title: "Reward Claimed!", description: "Check your email for confirmation." });
      setShowShippingForm(false);
      setSelectedPhysicalReward(null);
      setShippingDetails({ fullName: "", phone: "", address: "", pincode: "" });
      setActiveTab("my_rewards");
    } else {
      toast({ title: "Something went wrong", variant: "destructive" });
    }
  };

  // ─── DailyHisaab Store — Ultimate cash-only view ───
  if (isUltimate) {
    const storeItems = PHYSICAL_REWARDS.filter((r) => r.price !== null && typeof r.price === "number");

    return (
      <div className="flex flex-col min-h-screen bg-[#FFFBF2] dark:bg-[#1A1614] pb-24">
        <header className="px-4 py-4 flex items-center justify-between sticky top-0 z-30 bg-[#FFFBF2]/90 dark:bg-[#1A1614]/90 backdrop-blur-md border-b border-slate-200/50">
          <button onClick={handleBack} className="w-10 h-10 rounded-full bg-white dark:bg-slate-900 flex items-center justify-center">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="text-right">
            <h1 className="text-base font-black tracking-tight text-[#0F172A] dark:text-white">DailyHisaab Store</h1>
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Hardware + essentials</p>
          </div>
        </header>

        <main className="px-4 py-6 space-y-5 max-w-md mx-auto w-full">
          <div className="space-y-2">
            <h2 className="text-3xl font-black text-[#0F172A] dark:text-white tracking-tighter uppercase leading-tight">
              Shop <span className="text-[#8B1A1A]">Essentials</span>
            </h2>
            <p className="text-slate-500 text-sm font-medium leading-relaxed">
              Thermal printer, paper rolls, and more. Razorpay checkout. We'll contact you for shipping details.
            </p>
          </div>

          <div className="space-y-4">
            {storeItems.map((item) => (
              <div key={item.id} className="p-5 rounded-[24px] bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 shadow-sm">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-14 h-14 rounded-2xl bg-slate-50 dark:bg-slate-800 flex items-center justify-center shrink-0 border border-slate-100 dark:border-slate-700">
                    {item.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-black text-sm text-[#0F172A] dark:text-white leading-tight">{item.name}</p>
                    <p className="text-2xl font-black text-[#8B1A1A] mt-2">₹{item.price}</p>
                  </div>
                </div>
                <button
                  onClick={() => handlePhysicalCashPayment({ id: item.id, name: item.name, price: item.price as number })}
                  disabled={isProcessingPayment}
                  className="w-full h-12 rounded-2xl bg-[#8B1A1A] hover:bg-[#631212] disabled:opacity-50 text-white font-black uppercase tracking-widest text-xs flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
                >
                  {isProcessingPayment ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Buy Now <ChevronRight className="w-4 h-4" /></>}
                </button>
              </div>
            ))}
          </div>

          <div className="p-4 rounded-2xl bg-white/50 dark:bg-slate-900/50 border border-slate-200/80 dark:border-slate-800 flex items-start gap-3">
            <ShieldCheck className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
            <p className="text-[11px] text-slate-500 font-medium leading-relaxed">
              Secure Razorpay checkout. Delivery in 5-7 business days. Email <strong>support@dailyhisaab.com</strong> for order status.
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-[#FFFBF2] dark:bg-[#1A1614]">
      <header className="px-4 py-4 flex items-center justify-between sticky top-0 z-30 bg-[#FFFBF2]/90 dark:bg-[#1A1614]/90 backdrop-blur-md border-b border-slate-200/50">
        <div className="flex items-center">
          <button
            onClick={() => activeTab === 'history' ? setActiveTab('redeem') : handleBack()}
            className="w-10 h-10 bg-white dark:bg-slate-800 rounded-full flex items-center justify-center shadow-sm border border-slate-100 dark:border-slate-700 active:scale-95 transition-transform shrink-0"
          >
            <ChevronLeft className="w-6 h-6 text-slate-800 dark:text-white" />
          </button>
          <h1 className="ml-4 text-xl font-black text-slate-900 dark:text-white tracking-tight">
            {activeTab === 'history' ? 'History' : 'Rewards Hub'}
          </h1>
        </div>
        {activeTab !== 'history' && (
          <button
            onClick={() => setActiveTab('history')}
            className="bg-[#8B1A1A] hover:bg-[#631212] text-white px-5 py-2 rounded-full font-bold text-xs uppercase tracking-widest flex items-center shadow-lg active:scale-95 transition-all"
          >
            History <History className="w-3.5 h-3.5 ml-2" />
          </button>
        )}
      </header>

      <div className="flex-1 px-4 py-6 pb-24 overflow-y-auto">
        <div className="relative mb-8">
          <div className="bg-gradient-to-br from-[#FFFBF2] to-white dark:from-[#1A1614] dark:to-black border border-slate-100 dark:border-slate-800 rounded-[32px] p-8 shadow-lg shadow-slate-200/20 dark:shadow-black/40 relative overflow-hidden text-slate-900 dark:text-white">
            <div className="absolute inset-0 bg-white/5 backdrop-blur-[1px]" />
            <Sparkles className="absolute top-4 right-4 w-12 h-12 text-amber-400 opacity-20 rotate-12" />

            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center backdrop-blur-md border border-white/20">
                  <Zap className="h-4 w-4 text-amber-400 fill-amber-400" />
                </div>
                <span className="text-[10px] font-black tracking-[0.2em] uppercase opacity-70">Loyalty Balance</span>
              </div>

              <div className="flex items-baseline gap-3 mb-6">
                <span className="text-5xl font-black tracking-tighter">{totalPoints.toLocaleString()}</span>
                <span className="text-lg font-bold text-amber-400">Points</span>
              </div>

              <div className="flex items-center justify-between border-t border-white/10 pt-5">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${isCurrentlyPro ? 'bg-emerald-400 animate-pulse' : 'bg-slate-400'}`} />
                  <span className="text-[11px] font-bold uppercase tracking-wider opacity-80">
                    {isCurrentlyPro ? 'Premium Member' : 'Standard Member'}
                  </span>
                </div>
                {isCurrentlyPro && proModeExpiry && (
                  <span className="text-[10px] font-black bg-white/10 px-3 py-1 rounded-full border border-white/10">
                    VALID: {new Date(proModeExpiry).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }).toUpperCase()}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {activeTab !== 'history' && (
          <div className="flex bg-slate-200/50 dark:bg-slate-800/50 p-1.5 rounded-2xl mb-8 border border-slate-200 dark:border-slate-800">
            <button
              onClick={() => setActiveTab("redeem")}
              className={`flex-1 py-3 text-xs font-black uppercase tracking-widest rounded-xl transition-all ${activeTab === "redeem" ? "bg-white dark:bg-slate-900 text-[#8B1A1A] shadow-md border border-slate-100/50" : "text-slate-500"}`}
            >
              Redeem
            </button>
            <button
              onClick={() => setActiveTab("my_rewards")}
              className={`flex-1 py-3 text-xs font-black uppercase tracking-widest rounded-xl transition-all ${activeTab === "my_rewards" ? "bg-white dark:bg-slate-900 text-[#8B1A1A] shadow-md border border-slate-100/50" : "text-slate-500"}`}
            >
              My Rewards
            </button>
          </div>
        )}

        <AnimatePresence mode="wait">
          {activeTab === "redeem" && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-10">

              {/* DIGITAL PASSES */}
              <div>
                <h3 className="text-[11px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] mb-4 px-1 flex items-center gap-2">
                  <Crown className="w-4 h-4 text-amber-500" /> Premium Passes
                </h3>

                {/* 🛡️ EXPLANATORY UI FOR ACTIVE SUBSCRIBERS */}
                {isControllableSub && (
                  <div className="mb-5 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-2xl flex items-start gap-3 shadow-sm">
                    <ShieldCheck className="w-5 h-5 text-amber-600 dark:text-amber-500 shrink-0 mt-0.5" />
                    <p className="text-[11px] text-amber-800 dark:text-amber-300 font-medium leading-relaxed">
                      You have an active auto-renewing subscription. To use points for free days, please cancel your active subscription first, or use your points for Physical Rewards below.
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  {PRO_PLANS.map((plan) => {
                    const canAfford = totalPoints >= plan.points;
                    const isBlocked = isControllableSub;

                    return (
                      <div key={plan.days} className={`p-4 rounded-[24px] border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm flex flex-col items-center justify-center relative overflow-hidden ${isBlocked ? 'opacity-60 grayscale-[0.5]' : ''}`}>

                        {plan.days === 365 && !isBlocked && (
                          <div className="absolute top-0 right-0 bg-amber-500 text-white text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded-bl-lg shadow-sm">
                            Best Value
                          </div>
                        )}

                        <div className="text-sm font-black text-slate-900 dark:text-white mb-3 text-center">
                          {isCurrentlyPro && !isBlocked ? `+${plan.days} Days` : plan.label}
                        </div>

                        <div className="w-full mt-1">
                          {isBlocked ? (
                            <div className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl font-bold text-xs border border-slate-200 bg-slate-100 text-slate-500 dark:border-slate-700 dark:bg-slate-800 select-none">
                              <Lock className="h-3 w-3" />
                              Locked
                            </div>
                          ) : canAfford ? (
                            <button
                              onClick={() => setSelectedPlan(plan as any)}
                              className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl font-bold text-xs transition-transform border border-amber-200 bg-amber-50 hover:bg-amber-100 text-amber-700 dark:border-amber-700/50 dark:bg-amber-900/30 dark:text-amber-400 active:scale-95"
                            >
                              <Zap className="h-3 w-3 fill-amber-500 text-amber-500" />
                              {plan.points.toLocaleString()} pts
                            </button>
                          ) : (
                            <div className="space-y-2">
                              <div className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl font-bold text-xs border border-slate-100 bg-slate-50 text-slate-400 dark:border-slate-800 dark:bg-slate-900/50 dark:text-slate-500 select-none">
                                <Lock className="h-3 w-3" />
                                {plan.points.toLocaleString()} pts
                              </div>
                              <div className="flex items-center gap-2 px-1">
                                <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                  <motion.div initial={{ width: 0 }} animate={{ width: `${Math.min((totalPoints / plan.points) * 100, 100)}%` }} className="h-full bg-amber-400 rounded-full" />
                                </div>
                                <span className="text-[9px] font-black text-slate-400">{Math.floor(Math.min((totalPoints / plan.points) * 100, 100))}%</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* PHYSICAL REWARDS */}
              <div>
                <h3 className="text-[11px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] mb-4 px-1 flex items-center gap-2">
                  <Gift className="w-4 h-4 text-[#8B1A1A]" /> Physical Rewards
                </h3>
                <div className="space-y-4">
                  {PHYSICAL_REWARDS.map((item) => {
                    const canAfford = totalPoints >= item.points;

                    return (
                      <div key={item.id} className="bg-white dark:bg-slate-900 rounded-[24px] border border-slate-200 dark:border-slate-800 p-5 shadow-sm">
                        <div className="flex items-center gap-4 mb-4">
                          <div className="w-14 h-14 rounded-2xl bg-[#FFFBF2] dark:bg-slate-800 flex items-center justify-center border border-slate-100 dark:border-slate-700 shrink-0">{item.icon}</div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-bold text-sm text-slate-900 dark:text-white leading-tight truncate">{item.name}</h4>
                            <p className="text-[10px] text-amber-600 font-black mt-1 uppercase tracking-widest">{item.points.toLocaleString()} pts</p>
                          </div>

                          {/* DYNAMIC BUTTON LOGIC */}
                          <div className="shrink-0">
                            {item.price ? (
                              canAfford ? (
                                isCurrentlyPro ? (
                                  <button onClick={() => setSelectedPhysicalReward(item)} className="bg-gradient-to-r from-[#8B1A1A] to-amber-500 px-4 py-2 rounded-full text-[10px] font-black uppercase text-white flex items-center gap-1 shadow-md active:scale-95 transition-transform">
                                    <Unlock className="w-3 h-3" /> Redeem
                                  </button>
                                ) : (
                                  // 🚨 UPDATED TO CALL GLOBAL MODAL
                                  <button onClick={openProUpgradeModal} className="bg-gradient-to-r from-amber-400 to-amber-600 px-4 py-2 rounded-full text-[10px] font-black uppercase text-white flex items-center gap-1 shadow-md active:scale-95 transition-transform">
                                    <Crown className="w-3 h-3" /> Subscribe
                                  </button>
                                )
                              ) : (
                                <button disabled={isProcessingPayment} onClick={() => handlePhysicalCashPayment({ id: item.id, name: item.name, price: item.price! })} className="bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-4 py-2 rounded-full text-[10px] font-black uppercase flex items-center gap-1 shadow-md active:scale-95 transition-transform disabled:opacity-50">
                                  {isProcessingPayment ? <Loader2 className="w-3 h-3 animate-spin" /> : `Buy ₹${item.price}`}
                                </button>
                              )
                            ) : (
                              canAfford ? (
                                isCurrentlyPro ? (
                                  <button onClick={() => setSelectedPhysicalReward(item)} className="bg-gradient-to-r from-[#8B1A1A] to-amber-500 px-4 py-2 rounded-full text-[10px] font-black uppercase text-white flex items-center gap-1 shadow-md active:scale-95 transition-transform">
                                    <Unlock className="w-3 h-3" /> Redeem
                                  </button>
                                ) : (
                                  // 🚨 UPDATED TO CALL GLOBAL MODAL
                                  <button onClick={openProUpgradeModal} className="bg-gradient-to-r from-amber-400 to-amber-600 px-4 py-2 rounded-full text-[10px] font-black uppercase text-white flex items-center gap-1 shadow-md active:scale-95 transition-transform">
                                    <Crown className="w-3 h-3" /> Subscribe
                                  </button>
                                )
                              ) : (
                                <div className="bg-slate-100 dark:bg-slate-800 px-3 py-1.5 rounded-full text-[9px] font-black uppercase text-slate-400 flex items-center gap-1 border border-slate-200/50">
                                  <Lock className="w-2.5 h-2.5" /> Locked
                                </div>
                              )
                            )}
                          </div>
                        </div>

                        {/* Progress Bar */}
                        {!canAfford && (
                          <div className="space-y-1.5 mt-2">
                            <div className="flex justify-between text-[9px] font-black uppercase tracking-tighter text-slate-400">
                              <span>Point Progress</span>
                              <span>{Math.floor(Math.min((totalPoints / item.points) * 100, 100))}%</span>
                            </div>
                            <div className="h-2 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden border border-slate-200/50 dark:border-slate-700/50">
                              <motion.div initial={{ width: 0 }} animate={{ width: `${Math.min((totalPoints / item.points) * 100, 100)}%` }} className="h-full rounded-full bg-gradient-to-r from-[#8B1A1A] to-amber-500" />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === "my_rewards" && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
              <div className="bg-white dark:bg-slate-900 rounded-[32px] border border-slate-200 dark:border-slate-800 overflow-hidden shadow-sm">
                {myRewardsList.length === 0 ? (
                  <div className="py-32 text-center">
                    <Award className="h-16 w-16 text-slate-100 mx-auto mb-4" />
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Your trophy case is empty</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-50 dark:divide-slate-800/50">
                    {myRewardsList.map((item) => (
                      <div key={item.id} className="p-5 flex items-center justify-between hover:bg-slate-50/50 transition-colors">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${item.points < 0 ? "bg-amber-100 text-amber-600 border border-amber-200/50" : "bg-emerald-50 text-emerald-500 border border-emerald-100/50"}`}>
                            {item.points < 0 ? <Award className="h-6 w-6" /> : <Gift className="h-6 w-6" />}
                          </div>
                          <div>
                            <span className="font-bold text-sm text-slate-800 dark:text-slate-200">{item.description}</span>
                            <p className="text-[10px] font-medium text-slate-400 mt-0.5">{item.date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {activeTab === "history" && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-6">
              <div className="bg-white dark:bg-slate-900 rounded-[32px] border border-slate-200 dark:border-slate-800 overflow-hidden shadow-sm">
                <div className="p-5 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center">
                  <h3 className="text-xs font-black text-slate-900 dark:text-white uppercase tracking-[0.2em]">Points Ledger</h3>
                  <TrendingUp className="w-4 h-4 text-emerald-500" />
                </div>
                <div className="divide-y divide-slate-50 dark:divide-slate-800/50">
                  {rewardHistory.length === 0 ? (
                    <div className="py-20 text-center">
                      <Zap className="h-10 w-10 text-slate-200 mx-auto mb-3" />
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">No activity yet</p>
                    </div>
                  ) : (
                    <>
                      {rewardHistory.slice(0, earnHistoryCount).map((item) => (
                        <div key={item.id} className="p-5 flex items-center justify-between transition-colors hover:bg-slate-50/50">
                          <div className="flex items-center gap-4 min-w-0">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${item.points < 0 ? "bg-amber-100 text-amber-600" : "bg-emerald-50 text-emerald-500"}`}>
                              {item.points < 0 && (item.action === "pro_upgrade" || item.action === "physical_reward" || item.description.toLowerCase().includes("upgrade") || item.description.toLowerCase().includes("voucher") || item.description.toLowerCase().includes("printer") || item.description.toLowerCase().includes("thermal")) ? (
                                <Award className="h-5 w-5" />
                              ) : (
                                <Zap className="h-5 w-5 fill-current" />
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="font-bold text-sm text-slate-700 dark:text-slate-200 truncate">{item.description}</p>
                              <p className="text-[10px] font-medium text-slate-400 mt-0.5">{item.date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</p>
                            </div>
                          </div>
                          <div className={`font-black text-sm ${item.points < 0 ? "text-slate-500" : "text-emerald-500"}`}>
                            {item.points < 0 ? "" : "+"}{item.points}
                          </div>
                        </div>
                      ))}
                      {earnHistoryCount < rewardHistory.length && (
                        <button onClick={() => setEarnHistoryCount(p => p + 50)} className="w-full py-4 text-[10px] font-black text-[#8B1A1A] uppercase tracking-[0.2em] bg-slate-50/50 hover:bg-slate-100 transition-colors">
                          View Older History
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Points Redemption Confirmation Dialog for Digital Passes */}
      <AlertDialog open={!!selectedPlan} onOpenChange={(open) => !open && setSelectedPlan(null)}>
        <AlertDialogContent className="rounded-[32px] border-none p-8">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-2xl font-black text-center text-slate-900 dark:text-white tracking-tight">Confirm Redemption</AlertDialogTitle>
            <AlertDialogDescription className="text-center pt-4 text-slate-500">
              You are about to redeem <span className="text-[#8B1A1A] font-black">{selectedPlan?.points.toLocaleString()} points</span> for <br />
              <span className="inline-block mt-2 bg-amber-50 text-amber-700 px-4 py-1.5 rounded-full font-black text-[11px] uppercase tracking-widest border border-amber-100">
                {isCurrentlyPro ? `Extend Premium by ${selectedPlan?.days} Days` : selectedPlan?.label}
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row gap-3 justify-center mt-8">
            <AlertDialogCancel className="flex-1 rounded-2xl h-14 font-bold border-slate-200" onClick={() => setSelectedPlan(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="flex-[1.5] rounded-2xl h-14 bg-[#8B1A1A] hover:bg-[#631212] text-white font-black uppercase tracking-widest text-[11px]"
              onClick={handleConfirmUpgrade}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!selectedPhysicalReward && !showShippingForm && !showVoucherSuccess} onOpenChange={(open) => {
        if (!open) {
          setSelectedPhysicalReward(null);
          setIsSubmittingReward(false);
        }
      }}>
        <AlertDialogContent className="rounded-[32px] border-none p-8">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-2xl font-black text-center text-slate-900 dark:text-white tracking-tight">Redeem Reward</AlertDialogTitle>
            <AlertDialogDescription className="text-center pt-4 text-slate-500">
              You are about to redeem <span className="text-[#8B1A1A] font-black">{selectedPhysicalReward?.points.toLocaleString()} points</span> for <br />
              <span className="inline-block mt-2 bg-amber-50 text-amber-700 px-4 py-1.5 rounded-full font-black text-[11px] uppercase tracking-widest border border-amber-100">
                {selectedPhysicalReward?.name}
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row gap-3 justify-center mt-8">
            <AlertDialogCancel disabled={isSubmittingReward} className="flex-1 rounded-2xl h-14 font-bold border-slate-200" onClick={() => setSelectedPhysicalReward(null)}>Cancel</AlertDialogCancel>
            <button
              disabled={isSubmittingReward}
              className="flex-[1.5] rounded-2xl h-14 bg-[#8B1A1A] hover:bg-[#631212] text-white font-black uppercase tracking-widest text-[11px] flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
              onClick={handlePhysicalProceed}
            >
              {isSubmittingReward ? <Loader2 className="w-4 h-4 animate-spin" /> : "Proceed"}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showVoucherSuccess} onOpenChange={(open) => {
        if (!open) {
          setShowVoucherSuccess(false);
          setSelectedPhysicalReward(null);
        }
      }}>
        <AlertDialogContent className="rounded-[32px] border-none p-8">
          <AlertDialogHeader>
            <div className="mx-auto w-16 h-16 bg-red-50 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-[#8B1A1A]" />
            </div>
            <AlertDialogTitle className="text-2xl font-black text-center text-slate-900 dark:text-white tracking-tight">Reward Confirmed!</AlertDialogTitle>
            <AlertDialogDescription className="text-center pt-2 text-slate-500">
              We have received your order.
              <br /><br />
              You will receive your <strong className="text-slate-700 dark:text-slate-300">{selectedPhysicalReward?.name}</strong> at your registered email address within the next <strong className="text-amber-600">24 - 72 hours</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row justify-center mt-6">
            <AlertDialogAction
              className="w-full rounded-2xl h-14 bg-[#8B1A1A] hover:bg-[#631212] text-white font-black uppercase tracking-widest text-[11px]"
              onClick={() => {
                setShowVoucherSuccess(false);
                setSelectedPhysicalReward(null);
              }}
            >
              Got It
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={showShippingForm} onOpenChange={(open) => !open && !isSubmittingReward && setShowShippingForm(false)}>
        <DialogContent className="rounded-[32px] border-none shadow-2xl bg-[#FFFDF5] dark:bg-slate-900 p-6 sm:max-w-md">
          <DialogTitle className="text-xl font-extrabold text-slate-900 dark:text-white">Shipping Details</DialogTitle>
          <p className="text-sm text-slate-500 mb-4">Where should we send your {selectedPhysicalReward?.name}?</p>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-400 uppercase ml-1">Full Name <span className="text-red-500">*</span></Label>
              <Input className="h-12 rounded-2xl" value={shippingDetails.fullName} onChange={(e) => setShippingDetails({ ...shippingDetails, fullName: e.target.value })} placeholder="John Doe" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-400 uppercase ml-1">Phone Number <span className="text-red-500">*</span></Label>
              <div className="relative flex items-center">
                <div className="absolute left-4 font-bold text-slate-500 pointer-events-none">+91</div>
                <Input
                  className="h-12 rounded-2xl pl-12"
                  type="tel"
                  maxLength={10}
                  value={shippingDetails.phone.replace("+91", "").trim()}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setShippingDetails({ ...shippingDetails, phone: val ? `+91 ${val}` : "" });
                  }}
                  placeholder="9233128787"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-400 uppercase ml-1">Complete Address <span className="text-red-500">*</span></Label>
              <textarea className="w-full min-h-[80px] p-3 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B1A1A]" value={shippingDetails.address} onChange={(e) => setShippingDetails({ ...shippingDetails, address: e.target.value })} placeholder="House/Flat No, Street, Landmark..." />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-400 uppercase ml-1">PIN Code <span className="text-red-500">*</span></Label>
              <Input
                className="h-12 rounded-2xl"
                type="text"
                maxLength={6}
                value={shippingDetails.pincode}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setShippingDetails({ ...shippingDetails, pincode: val });
                }}
                placeholder="110001"
              />
            </div>

            <button
              disabled={
                isSubmittingReward ||
                !shippingDetails.fullName.trim() ||
                !shippingDetails.address.trim() ||
                (shippingDetails.phone.replace("+91", "").trim().length !== 10) ||
                (shippingDetails.pincode.length !== 6)
              }
              onClick={handleShippingSubmit}
              className="w-full mt-2 bg-gradient-to-r from-[#8B1A1A] to-[#631212] hover:from-[#631212] hover:to-[#4A0B0B] text-white py-4 rounded-2xl font-black uppercase tracking-widest text-[11px] flex items-center justify-center gap-2 disabled:opacity-50 transition-all shadow-lg shadow-red-900/20"
            >
              {isSubmittingReward ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit & Redeem"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showSwitchToProDialog} onOpenChange={setShowSwitchToProDialog}>
        <AlertDialogContent className="rounded-[32px] border-none p-8">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-2xl font-black text-center text-slate-900 dark:text-white tracking-tight">Switch to Pro?</AlertDialogTitle>
            <AlertDialogDescription className="text-center pt-4 text-slate-500">
              You already have an active Pro membership! Would you like to switch your account to Pro mode now so you can redeem physical rewards?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row gap-3 justify-center mt-8">
            <AlertDialogCancel className="flex-1 rounded-2xl h-14 font-bold border-slate-200">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="flex-[1.5] rounded-2xl h-14 bg-[#8B1A1A] hover:bg-[#631212] text-white font-black uppercase tracking-widest text-[11px]"
              onClick={() => {
                setUserMode("pro");
                toast({ title: "Switched to Pro Mode!" });
                setShowSwitchToProDialog(false);
              }}
            >
              Switch Now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}